import { useAppApolloClient } from "hooks/apollo/apollo";
import {
  CONSULT_REQUEST_ADD_NOTE,
  CONSULT_REQUEST_ASSIGN,
  CONSULT_REQUEST_CHANGE_STATUS,
  CONSULT_REQUEST_DELETE_FILE,
  CONSULT_REQUEST_UPLOAD_FILE,
} from "graphql/consultRequest/mutation";
import {
  CONSULT_REQUEST_ASSIGNEES,
  CONSULT_REQUEST_DETAIL,
  CONSULT_REQUEST_DOWNLOAD_FILE,
  CONSULT_REQUEST_FILES,
  CONSULT_REQUEST_HISTORIES,
  CONSULT_REQUEST_LIST,
  CONSULT_REQUEST_NOTES,
  CONSULT_REQUEST_PROJECT_TYPE_OPTIONS,
  CONSULT_REQUEST_STATUS_COUNTS,
} from "graphql/consultRequest/query";
import {
  convertAssignees,
  convertConsultRequestDetail,
  convertConsultRequestList,
  convertDeleteFile,
  convertDownloadFile,
  convertFiles,
  convertHistories,
  convertMutationResult,
  convertNotes,
  convertProjectTypeOptions,
  convertStatusCounts,
} from "./converter";

// Phiếu tư vấn giữ hành vi REST cũ: 401 chỉ báo lỗi tại màn, không tự đăng xuất (xem errorLink trong apollo.js).
export const CONSULT_CONTEXT = { skipAuthLogout: true };

/** Lỗi GraphQL trả kèm data (errorPolicy=all) được ném ra như lỗi reject để không bị coi là thành công. */
export class ConsultGraphQLError extends Error {
  constructor(graphQLErrors) {
    super(graphQLErrors?.[0]?.message || "GraphQL error");
    this.graphQLErrors = graphQLErrors;
  }
}

const unwrap = (convert) => (result) => {
  if (result?.errors?.length) throw new ConsultGraphQLError(result.errors);
  return convert(result?.data);
};

const query = (client, document, variables, convert, context = {}) =>
  client
    .query({
      query: document,
      variables,
      fetchPolicy: "network-only",
      errorPolicy: "all",
      context: { ...CONSULT_CONTEXT, ...context },
    })
    .then(unwrap(convert));

const mutate = (client, document, variables, convert, context = {}) =>
  client
    .mutate({ mutation: document, variables, errorPolicy: "all", context: { ...CONSULT_CONTEXT, ...context } })
    .then(unwrap(convert));

const compact = (obj) => Object.fromEntries(Object.entries(obj || {}).filter(([, v]) => v !== undefined));

/** Tách params kiểu REST (filter phẳng + page/size) thành biến GraphQL { filter, page, size }. */
export const toListVariables = ({ page, size, ...filter } = {}) =>
  compact({ filter: Object.keys(compact(filter)).length ? compact(filter) : undefined, page, size });

export const fetchConsultRequests = (client, params) =>
  query(client, CONSULT_REQUEST_LIST, toListVariables(params), convertConsultRequestList);
export const fetchStatusCounts = (client, filter) =>
  query(client, CONSULT_REQUEST_STATUS_COUNTS, { filter: toListVariables(filter).filter }, convertStatusCounts);
export const fetchProjectTypeOptions = (client) =>
  query(client, CONSULT_REQUEST_PROJECT_TYPE_OPTIONS, {}, convertProjectTypeOptions);
export const fetchConsultRequest = (client, id) =>
  query(client, CONSULT_REQUEST_DETAIL, { id }, convertConsultRequestDetail);
export const fetchAssignees = (client, keyword) =>
  query(client, CONSULT_REQUEST_ASSIGNEES, compact({ keyword: keyword || undefined }), convertAssignees);
export const fetchNotes = (client, id, { page, size } = {}) =>
  query(client, CONSULT_REQUEST_NOTES, compact({ id, page, size }), convertNotes);
export const fetchFiles = (client, id) => query(client, CONSULT_REQUEST_FILES, { id }, convertFiles);
export const fetchDownloadFile = (client, id, fileId) =>
  query(client, CONSULT_REQUEST_DOWNLOAD_FILE, { id, fileId }, convertDownloadFile);
export const fetchHistories = (client, id, { action, page, size } = {}) =>
  query(client, CONSULT_REQUEST_HISTORIES, compact({ id, action, page, size }), convertHistories);

export const mutateAssign = (client, id, { assigneeId, version }) =>
  mutate(client, CONSULT_REQUEST_ASSIGN, { id, assigneeId, version }, convertMutationResult("assign"));
export const mutateChangeStatus = (client, id, { toStatus, reasonCode, reasonNote, version }) =>
  mutate(
    client,
    CONSULT_REQUEST_CHANGE_STATUS,
    compact({ id, toStatus, reasonCode, reasonNote, version }),
    convertMutationResult("changeStatus")
  );
export const mutateAddNote = (client, id, content) =>
  mutate(client, CONSULT_REQUEST_ADD_NOTE, { id, content }, convertMutationResult("addNote"));
// fetchOptions đi vào fetch của createUploadLink (uploadProgressFetch) để báo tiến độ và hủy.
export const mutateUploadFile = (client, id, file, { onUploadProgress, signal } = {}) =>
  mutate(client, CONSULT_REQUEST_UPLOAD_FILE, { id, file }, convertMutationResult("uploadFile"), {
    fetchOptions: compact({ onUploadProgress, signal }),
  });
export const mutateDeleteFile = (client, id, fileId) =>
  mutate(client, CONSULT_REQUEST_DELETE_FILE, { id, fileId }, convertDeleteFile);

export function useConsultApolloClient() {
  const { apolloClient } = useAppApolloClient();
  return apolloClient;
}
